import { Flex, List, Tooltip, Typography } from 'antd';
import { type ReactNode } from 'react';
import {
  type InvoicesExpandedRowState,
  type RequestParamsOfGetInvoicesRequest,
  type InvoicesPageState,
  type RequestParamsOfGetCustomerMonthInvoicesRequest,
} from './types';
import { type SiteInvoice } from '../../../entities';

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_NUMBER = 1;

// Reducer Method to set the state in both InvoicesPage and InvoiceExpandedRow
export const renderSitesColumn = (siteInvoices: Readonly<SiteInvoice[]>): ReactNode => {
  // Get list of all sites, to be shown on the tooltip
  const sitesListItems = siteInvoices
    .map((siteInvoice) => siteInvoice?.site?.name ?? '')
    .map((siteName, index) => <List.Item key={index}> {siteName}</List.Item>);

  // Get comma separated string of the the first 3 sites names.
  const firstThreeSites = siteInvoices
    .slice(0, 3)
    .map((siteInvoice) => siteInvoice?.site?.name ?? '')
    .join(',');

  // Add more sites hint
  const firstThreeSitesWithHint =
    firstThreeSites + (siteInvoices.length > 3 ? ', and more....' : '');

  return (
    <Tooltip
      title={
        <Flex gap="small" style={{ maxHeight: 400, overflowY: 'auto' }} vertical>
          <List split={false}>{sitesListItems}</List>
        </Flex>
      }
    >
      <Typography.Text>{firstThreeSitesWithHint}</Typography.Text>
    </Tooltip>
  );
};

// Gets the request params for Get CustomerMonthInvoicesRequest
export const getRequestParamsForGetCustomerMonthInvoicesRequest = (
  state: Readonly<InvoicesPageState>
): RequestParamsOfGetCustomerMonthInvoicesRequest => {
  const requestParams: RequestParamsOfGetCustomerMonthInvoicesRequest = {
    pageNumber: state.pagination.current ?? DEFAULT_PAGE_NUMBER,
    pageSize: state.pagination.pageSize ?? DEFAULT_PAGE_SIZE,
  };
  const filters = state.filters;
  if (!filters) {
    return requestParams;
  }

  if (filters.customers) {
    requestParams.customers = filters.customers.join(',');
  }

  if (filters.statuses) {
    requestParams.statuses = filters.statuses.join(',');
  }

  if (filters.billMonth) {
    requestParams.startDate = filters.billMonth?.[0];
    requestParams.endDate = filters.billMonth?.[1];
  }

  return requestParams;
};

// Gets the request params for Get InvoicesByCustomerMonthInvoiceId
export const getRequestParamsForGetInvoicesRequest = (
  state: Readonly<InvoicesExpandedRowState>
): RequestParamsOfGetInvoicesRequest => {
  const requestParams: RequestParamsOfGetInvoicesRequest = {};
  const filters = state.filters;
  if (!filters) {
    return requestParams;
  }
  if (filters.statuses) {
    requestParams.statuses = filters.statuses.join(',');
  }
  return requestParams;
};
